import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useDarkMode } from '../../context/DarkModeContext';

function fmt(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/**
 * DualAxisLineChart — two lines with independent Y-axes.
 *
 * Props:
 *   data         – array of data objects
 *   leftKey      – dataKey for left Y-axis line
 *   rightKey     – dataKey for right Y-axis line
 *   leftLabel    – display name for left line
 *   rightLabel   – display name for right line
 *   leftColor    – stroke color for left line (default blue)
 *   rightColor   – stroke color for right line (default green)
 *   leftFormat   – formatter fn for left Y-axis ticks
 *   rightFormat  – formatter fn for right Y-axis ticks
 *   zeroLine     – if true, add ReferenceLine at y=0 on left axis
 *   height       – chart height in px
 */
export default function DualAxisLineChart({
  data,
  leftKey,
  rightKey,
  leftLabel,
  rightLabel,
  leftColor = '#3b82f6',
  rightColor = '#22c55e',
  leftFormat = (v) => v.toFixed(4),
  rightFormat = (v) => v.toFixed(6),
  zeroLine = false,
  height = 220,
  syncId,
}) {
  const { dark } = useDarkMode();

  if (!data || data.length === 0) return null;

  const grid = dark ? '#374151' : '#e5e7eb';
  const axis = dark ? '#6b7280' : '#9ca3af';
  const tip = dark
    ? { fontSize: 12, borderRadius: 8, backgroundColor: '#1f2937', border: '1px solid #374151', color: '#f3f4f6' }
    : { fontSize: 12, borderRadius: 8 };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} syncId={syncId} margin={{ top: 8, right: 48, bottom: 24, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis
          dataKey="date"
          tickFormatter={fmt}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          angle={-30}
          textAnchor="end"
          height={44}
        />
        <YAxis
          yAxisId="left"
          tickFormatter={leftFormat}
          tick={{ fontSize: 10, fill: leftColor }}
          stroke={leftColor}
          width={48}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tickFormatter={rightFormat}
          tick={{ fontSize: 10, fill: rightColor }}
          stroke={rightColor}
          width={48}
        />
        {zeroLine && (
          <ReferenceLine yAxisId="left" y={0} stroke={dark ? '#4b5563' : '#d1d5db'} strokeWidth={1.5} />
        )}
        <Tooltip contentStyle={tip} labelFormatter={fmt} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />

        <Line
          yAxisId="left"
          type="monotoneX"
          dataKey={leftKey}
          name={leftLabel}
          stroke={leftColor}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          yAxisId="right"
          type="monotoneX"
          dataKey={rightKey}
          name={rightLabel}
          stroke={rightColor}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
          strokeDasharray="5 3"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
